import {
  TprmAnswerConfig,
  TprmCondition,
  TprmExternalAnswer,
  TprmExternalFilesAnswer,
  TprmExternalTableAnswer,
  TprmExternalSelectAnswer,
  TprmExternalSubQuestionnairesAnswer,
  TprmExternalTextAnswer,
  TprmQuestionTemplateId,
  TprmSelectAnswerConfig,
  TprmTextAnswerConfig,
  TprmExternalResponse,
  TprmQuestionTemplate,
  TprmQuestionnaireTemplate,
  TprmExternalQuestionnaireExternal,
} from "platformed-client/types";
import {unreachableCase} from "../utils/typescript";
import {isColumnFilled} from "./components/table/tableUtils";
import {QuestionnaireCompletionStats} from "./types";

export function assertAnswerType<T extends TprmExternalAnswer["type"]>(
  answer: TprmExternalAnswer,
  type: T,
): asserts answer is Extract<TprmExternalAnswer, {type: T}> {
  if (answer.type !== type) {
    throw new Error(`Expected answer type ${type}, got ${answer.type}`);
  }
}

export function answerContent(answer: TprmExternalAnswer, type: "Text"): TprmExternalTextAnswer;
export function answerContent(answer: TprmExternalAnswer, type: "Select"): TprmExternalSelectAnswer;
export function answerContent(answer: TprmExternalAnswer, type: "Files"): TprmExternalFilesAnswer;
export function answerContent(
  answer: TprmExternalAnswer,
  type: "SubQuestionnaires",
): TprmExternalSubQuestionnairesAnswer;
export function answerContent(answer: TprmExternalAnswer, type: "Table"): TprmExternalTableAnswer;
export function answerContent(
  answer: TprmExternalAnswer,
  type: Exclude<TprmExternalAnswer["type"], "NotApplicable">,
):
  | TprmExternalTextAnswer
  | TprmExternalSelectAnswer
  | TprmExternalFilesAnswer
  | TprmExternalSubQuestionnairesAnswer
  | TprmExternalTableAnswer {
  assertAnswerType(answer, type);
  return answer.content;
}

// Empty answer checks

export function textAnswerIsEmpty(answer: TprmExternalTextAnswer): boolean {
  return answer.text.length === 0;
}

export function filesAnswerIsEmpty(answer: TprmExternalFilesAnswer): boolean {
  return answer.file_ids.length === 0;
}

export function selectAnswerIsEmpty(answer: TprmExternalSelectAnswer): boolean {
  return answer.selected.length === 0;
}

export function subQuestionnairesAnswerIsEmpty(answer: TprmExternalSubQuestionnairesAnswer): boolean {
  return answer.questionnaire_ids.length === 0;
}

export function tableAnswerIsEmpty(answer: TprmExternalTableAnswer): boolean {
  return answer.rows.length === 0;
}

export function answerIsEmpty(answer: TprmExternalAnswer): boolean {
  if (answer.type === "NotApplicable") {
    return false; // NotApplicable is considered a valid answer, not empty
  }

  switch (answer.type) {
    case "Text":
      return textAnswerIsEmpty(answer.content);
    case "Files":
      return filesAnswerIsEmpty(answer.content);
    case "Select":
      return selectAnswerIsEmpty(answer.content);
    case "SubQuestionnaires":
      return subQuestionnairesAnswerIsEmpty(answer.content);
    case "Table":
      return tableAnswerIsEmpty(answer.content);
    default:
      unreachableCase(answer);
  }
}

/**
 * An question is complete if its answer is non-empty and all of its constituent parts are complete:
 * - For `Text`, `Select` and `File` questions, this is the same as `!answerIsEmpty(answer)`.
 * - For `SubQuestionnaire` questions, all questions in the sub-questionnaire must also be complete.
 * - For `Table` questions, every row must have a non-empty value for each required column.
 */
export function questionIsComplete(
  template: TprmQuestionnaireTemplate,
  questionnaire: TprmExternalQuestionnaireExternal,
  config: TprmAnswerConfig,
  answer: TprmExternalAnswer,
): boolean {
  if (answerIsEmpty(answer)) {
    return false;
  }

  // NotApplicable is always considered complete regardless of the expected answer type
  if (answer.type === "NotApplicable") {
    return true;
  }

  switch (config.type) {
    case "Text":
    case "Select":
    case "Files":
      return true;
    case "Table": {
      assertAnswerType(answer, "Table");
      // Mirror the backend's submission check: every row must have a non-empty
      // value for each required column. With no required columns, a non-empty
      // table (guaranteed by the answerIsEmpty check above) is complete.
      const requiredColumns = config.content.columns.filter(c => c.is_required);
      return answer.content.rows.every(row => requiredColumns.every(column => isColumnFilled(row.values[column.key])));
    }
    case "SubQuestionnaires": {
      assertAnswerType(answer, "SubQuestionnaires");

      return answer.content.questionnaire_ids.every(subQuestionnaireId => {
        const subQuestionnaireTemplate = template.referenced_templates[config.content.questionnaire_template_id];
        const subQuestionnaire = questionnaire.referenced_questionnaires[subQuestionnaireId];

        // Sub-questionnaire data may not be populated yet if the cache is
        // mid-refetch (e.g. during suggestion application). Treat as incomplete.
        if (!subQuestionnaireTemplate || !subQuestionnaire) return false;

        return isQuestionnaireSubmittable(getQuestionnaireCompletionStats(subQuestionnaireTemplate, subQuestionnaire));
      });
    }
    default:
      unreachableCase(config);
  }
}

export function getQuestionnaireCompletionStats(
  template: TprmQuestionnaireTemplate,
  questionnaire: TprmExternalQuestionnaireExternal,
): QuestionnaireCompletionStats {
  // We only care about visible questions, so filter out all questions with
  // unmet conditions.
  const questions = template.sections
    .flatMap(s => s.questions)
    .filter(q => checkCondition(q.condition ?? undefined, template, questionnaire.responses));

  const completeQuestions = questions.filter(q => {
    const response = questionnaire.responses[q.question_template_id];
    return questionIsComplete(template, questionnaire, q.answer_config, response.answer);
  });

  const requiredQuestions = questions.filter(q => q.is_required);

  const completeRequiredQuestions = requiredQuestions.filter(q => {
    const response = questionnaire.responses[q.question_template_id];
    return questionIsComplete(template, questionnaire, q.answer_config, response.answer);
  });

  const unansweredRequiredQuestions = requiredQuestions.filter(q => {
    const response = questionnaire.responses[q.question_template_id];
    return !questionIsComplete(template, questionnaire, q.answer_config, response.answer);
  });

  const partiallyCompleteQuestions = questions.filter(q => {
    const response = questionnaire.responses[q.question_template_id];

    return (
      !answerIsEmpty(response.answer) && !questionIsComplete(template, questionnaire, q.answer_config, response.answer)
    );
  });

  const invalidQuestions = questions.filter(q => {
    const response = questionnaire.responses[q.question_template_id];
    return validateAnswer(q.answer_config, response.answer) != null;
  });

  return {
    questions,
    requiredQuestions,

    completeQuestions,
    completeRequiredQuestions,

    unansweredRequiredQuestions,
    partiallyCompleteQuestions,
    invalidQuestions,
  };
}

export function isQuestionnaireSubmittable(stats: QuestionnaireCompletionStats) {
  return (
    stats.unansweredRequiredQuestions.length === 0 &&
    stats.partiallyCompleteQuestions.length === 0 &&
    stats.invalidQuestions.length === 0
  );
}

// Validation

export type ValidationResult = string;

export function validateTextAnswer(
  config: TprmTextAnswerConfig,
  answer: TprmExternalTextAnswer,
): ValidationResult | null {
  if (textAnswerIsEmpty(answer)) {
    return null;
  }

  if (config.min_length != null && answer.text.length < config.min_length) {
    return `Too few characters (min: ${config.min_length}). Please lengthen your answer.`;
  }

  if (config.max_length != null && answer.text.length > config.max_length) {
    return `Too many characters (max: ${config.max_length}). Please shorten your answer.`;
  }

  return null;
}

export function validateSelectAnswer(
  config: TprmSelectAnswerConfig,
  answer: TprmExternalSelectAnswer,
): ValidationResult | null {
  if (selectAnswerIsEmpty(answer)) {
    return null;
  }

  if (config.min_number != null && answer.selected.length < config.min_number) {
    return `Too few selections (min: ${config.min_number}). Please select more options.`;
  }

  if (config.max_number != null && answer.selected.length > config.max_number) {
    return `Too many selections (max: ${config.max_number}). Please select fewer options.`;
  }

  return null;
}

export function validateAnswer(config: TprmAnswerConfig, answer: TprmExternalAnswer): ValidationResult | null {
  // NotApplicable is always valid (when the question allows it)
  if (answer.type === "NotApplicable") {
    return null;
  }

  switch (config.type) {
    case "Text": {
      assertAnswerType(answer, "Text");
      return validateTextAnswer(config.content, answer.content);
    }
    case "Select": {
      assertAnswerType(answer, "Select");
      return validateSelectAnswer(config.content, answer.content);
    }
    case "Files": {
      return null;
    }
    case "SubQuestionnaires": {
      return null;
    }
    case "Table": {
      // Structural validation happens on the backend at submit time.
      return null;
    }
    default:
      unreachableCase(config);
  }
}

// Misc

export function getQuestionSuffix(config: TprmAnswerConfig): string | null {
  switch (config.type) {
    case "Text": {
      // Text questions don't need a suffix because there is a separate
      // character count widget.
      return null;
    }
    case "Select":
    case "Files": {
      const c = config.content;

      // Don't need to show a suffix for single-select or single-file questions:
      // - For single-selects, it's already evident because we use radio buttons instead of checkboxes.
      // - For file uploads, it should be evident from the question wording, and
      // the wording on the "Add files" button changes to "Add file".
      if (c.max_number === 1) {
        return null;
      }

      if (c.min_number != null && c.max_number != null) {
        return ` (Min: ${c.min_number}, Max: ${c.max_number})`;
      }

      if (c.min_number != null) {
        return ` (Min: ${c.min_number})`;
      }

      if (c.max_number != null) {
        return ` (Max: ${c.max_number})`;
      }

      return null;
    }
    case "SubQuestionnaires": {
      return null;
    }
    case "Table": {
      return null;
    }
    default:
      unreachableCase(config);
  }
}

/**
 * This function should match the logic in the backend `check_condition`
 * function.
 */
export function checkCondition(
  condition: TprmCondition | undefined,
  template: TprmQuestionnaireTemplate,
  responses: Record<TprmQuestionTemplateId, TprmExternalResponse>,
): boolean {
  if (condition == null) {
    return true;
  }

  switch (condition.type) {
    case "Literal": {
      return condition.content;
    }
    case "SelectMatches": {
      const questionTemplate = findQuestionByKey(template, condition.content.question_template_key);

      if (questionTemplate == null) {
        // The condition should not be met if the referred question
        // could not be found.
        return false;
      }

      // The parent question's condition should also be met
      if (!checkCondition(questionTemplate.condition ?? undefined, template, responses)) {
        return false;
      }

      const response = responses[questionTemplate.question_template_id];

      if (response.answer.type != "Select") {
        return false;
      }

      const selected = response.answer.content.selected;
      const values = condition.content.values;

      switch (condition.content.criteria) {
        case "Exact": {
          return selected.length === values.length && selected.every(s => values.includes(s));
        }
        case "ContainsAll": {
          return values.every(v => selected.includes(v));
        }
        case "ContainsOnly": {
          return selected.every(s => values.includes(s));
        }
        case "ContainsAny": {
          return selected.some(s => values.includes(s));
        }
        case "NotContainsAny": {
          return !selected.some(s => values.includes(s));
        }
        default:
          unreachableCase(condition.content.criteria);
      }
    }
    case "Count": {
      const questionTemplate = findQuestionByKey(template, condition.content.question_template_key);

      if (questionTemplate == null) {
        // The condition should not be met if the referred question
        // could not be found.
        return false;
      }

      // The parent question's condition should also be met
      if (!checkCondition(questionTemplate.condition ?? undefined, template, responses)) {
        return false;
      }

      const response = responses[questionTemplate.question_template_id];

      const count = (() => {
        switch (response.answer.type) {
          case "Text":
            return response.answer.content.text.length;
          case "Select":
            return response.answer.content.selected.length;
          case "Files":
            return response.answer.content.file_ids.length;
          case "SubQuestionnaires":
            return response.answer.content.questionnaire_ids.length;
          case "Table":
            return response.answer.content.rows.length;
          case "NotApplicable":
            return 0; // Treat as zero for conditional logic
          default:
            unreachableCase(response.answer);
        }
      })();

      switch (condition.content.operator) {
        case "Equal":
          return count === condition.content.value;
        case "NotEqual":
          return count !== condition.content.value;
        case "LessThan":
          return count < condition.content.value;
        case "LessThanOrEqual":
          return count <= condition.content.value;
        case "GreaterThan":
          return count > condition.content.value;
        case "GreaterThanOrEqual":
          return count >= condition.content.value;
        default:
          unreachableCase(condition.content.operator);
      }
    }
    case "And": {
      return condition.content.every(c => checkCondition(c, template, responses));
    }
    case "Or": {
      return condition.content.some(c => checkCondition(c, template, responses));
    }
    case "Not": {
      return !checkCondition(condition.content, template, responses);
    }
    default:
      unreachableCase(condition);
  }
}

export function findQuestionByKey(template: TprmQuestionnaireTemplate, key: string): TprmQuestionTemplate | undefined {
  return template.sections.flatMap(s => s.questions).find(q => q.key === key);
}

/**
 * Check if any responses have AI validation in progress
 */
export function hasInProgressAIValidation(questionnaire: TprmExternalQuestionnaireExternal): boolean {
  return Object.values(questionnaire.responses).some(response => response.ai_validation_task != null);
}

/**
 * Whether a question's current AI feedback has been dismissed. Keyed by feedback text so that new
 * feedback (e.g. after an answer edit) is not considered dismissed.
 */
export function isFeedbackDismissed(
  dismissed: Map<TprmQuestionTemplateId, string>,
  questionId: TprmQuestionTemplateId,
  feedback: string | null | undefined,
): boolean {
  return feedback != null && dismissed.get(questionId) === feedback;
}

/**
 * Check if any responses have AI validation errors (feedback) that haven't been dismissed
 */
export function hasAIValidationErrors(
  questionnaire: TprmExternalQuestionnaireExternal,
  dismissed: Map<TprmQuestionTemplateId, string>,
): boolean {
  return Object.entries(questionnaire.responses).some(
    ([questionId, response]) =>
      response.ai_feedback != null && dismissed.get(questionId as TprmQuestionTemplateId) !== response.ai_feedback,
  );
}

/**
 * Count how many responses have AI validation errors (feedback) that haven't been dismissed
 */
export function countAIValidationErrors(
  questionnaire: TprmExternalQuestionnaireExternal,
  dismissed: Map<TprmQuestionTemplateId, string>,
): number {
  return Object.entries(questionnaire.responses).filter(
    ([questionId, response]) =>
      response.ai_feedback != null && dismissed.get(questionId as TprmQuestionTemplateId) !== response.ai_feedback,
  ).length;
}

/**
 * Count how many responses have AI validation in progress
 */
export function countInProgressAIValidation(questionnaire: TprmExternalQuestionnaireExternal): number {
  return Object.values(questionnaire.responses).filter(response => response.ai_validation_task != null).length;
}
